import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, MapPin, Music, Star, Edit, MessageCircle, UserPlus, Eye, CheckCircle, DollarSign, Ban } from "lucide-react";
import DOMPurify from "dompurify";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { ReportButton } from "@/components/ReportButton";

export default function Profile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");

  const isOwnProfile = currentUser?.user_id === userId;

  const fetchProfile = useCallback(async () => {
    try {
      const response = await fetch(`${API}/profile/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else {
        toast.error("Profile not found");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  const fetchFavorites = useCallback(async () => {
    try {
      const response = await fetch(`${API}/profile/${userId}/favorites`);
      if (response.ok) {
        const data = await response.json();
        setFavorites(data);
      }
    } catch (error) {
      toast.error("Failed to load favorites");
    }
  }, [userId]);

  useEffect(() => {
    fetchProfile();
    fetchFavorites();
  }, [fetchProfile, fetchFavorites]);

  const toggleFavorite = async () => {
    if (!currentUser) {
      navigate("/login");
      return;
    }

    try {
      const method = isFavorite ? "DELETE" : "POST";
      const response = await fetch(`${API}/profile/favorite/${userId}`, {
        method,
        credentials: "include",
      });

      if (response.ok) {
        setIsFavorite(!isFavorite);
        toast.success(isFavorite ? "Removed from Top Friends" : "Added to Top Friends!");
      }
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#FF00FF] text-xl animate-pulse">Loading profile...</div>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="min-h-screen relative z-10 pb-24" data-testid="profile-page">
      {/* Custom CSS Injection */}
      {profile.custom_css && (
        <style dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(profile.custom_css, { FORCE_BODY: true }) }} />
      )}

      {/* Back Button */}
      <div className="fixed top-4 left-4 z-50">
        <button
          onClick={() => navigate(-1)}
          className="myspace-btn px-2 py-1"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </div>

      {/* Profile Hero with glitter */}
      <div className="profile-hero">
        {profile.photos?.[0] && (
          <img
            src={profile.photos[0]}
            alt="Cover"
            className="w-full h-full object-cover opacity-30"
          />
        )}
        
        {/* Profile Picture */}
        <div className="profile-avatar overflow-hidden glitter-overlay">
          {profile.picture || profile.photos?.[0] ? (
            <img
              src={profile.picture || profile.photos[0]}
              alt={profile.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-5xl bg-[#000033]">
              😎
            </div>
          )}
        </div>
      </div>

      {/* Profile Content */}
      <div className="container mx-auto px-4 pt-20 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Basic Info */}
          <div className="space-y-4">
            {/* Name & Online Status */}
            <div className="myspace-card">
              <div className="flex items-center gap-2 mb-2">
                {profile.is_online && <span className="online-dot"></span>}
                <h1 className="text-2xl font-bold glitter-text" data-testid="profile-name">
                  {profile.name}
                </h1>
                {profile.artist_verified && (
                  <span className="text-[#FFD700]" title="Verified Artist" style={{ filter: "drop-shadow(0 0 6px #FFD700)" }} data-testid="verified-badge">
                    <CheckCircle className="w-6 h-6" />
                  </span>
                )}
              </div>
              
              {profile.is_artist && (
                <p className="text-[#FF66FF] text-sm flex items-center gap-1 mb-1">
                  <Music className="w-3 h-3" /> {profile.artist_genre || "Artist"}
                </p>
              )}
              
              {profile.age && (
                <p className="text-[#99CCFF]">{profile.age} years old</p>
              )}
              
              {profile.location?.city && (
                <p className="flex items-center gap-1 text-[#99CCFF]">
                  <MapPin className="w-4 h-4 text-[#FF66FF]" />
                  {profile.location.city}
                </p>
              )}

              {/* Mood */}
              <div className="mood-badge mt-3">
                <span>😊</span>
                <span>Mood: Happy</span>
              </div>

              {/* View Counter */}
              <div className="flex items-center gap-2 mt-3">
                <Eye className="w-4 h-4 text-[#6699CC]" />
                <span className="view-counter text-xs">
                  Profile Views: 1,337
                </span>
              </div>

              {/* Last Login */}
              <p className="text-xs text-[#6699CC] mt-2">
                Last Login: {profile.is_online ? "Online Now!" : "Today"}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="myspace-card">
              {isOwnProfile ? (
                <button
                  onClick={() => navigate("/edit-profile")}
                  className="myspace-btn-primary w-full"
                  data-testid="edit-profile-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Profile
                </button>
              ) : currentUser && (
                <div className="space-y-2">
                  <button
                    onClick={toggleFavorite}
                    className={`w-full ${isFavorite ? 'myspace-btn-primary' : 'myspace-btn'}`}
                    data-testid="favorite-btn"
                  >
                    <Star className="w-4 h-4 mr-2" fill={isFavorite ? "currentColor" : "none"} />
                    {isFavorite ? "★ In Top Friends" : "Add to Top Friends"}
                  </button>
                  <button className="myspace-btn w-full">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Send Message
                  </button>
                  <button className="myspace-btn w-full">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add as Friend
                  </button>
                  {profile.is_artist && (
                    <button onClick={() => navigate(`/tip/${profile.user_id}`)} className="myspace-btn w-full" style={{ borderColor: "#00FF00", color: "#00FF00" }} data-testid="tip-artist-btn">
                      <DollarSign className="w-4 h-4 mr-2" />
                      Tip Artist
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      const res = await fetch(`${API}/block/${profile.user_id}`, { method: "POST", credentials: "include" });
                      if (res.ok) { const d = await res.json(); toast.info(d.blocked ? "User blocked" : "User unblocked"); }
                    }}
                    className="myspace-btn w-full text-[#FF6666] border-[#FF6666]"
                    data-testid="block-user-btn"
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    Block
                  </button>
                  <ReportButton
                    targetType="user"
                    targetId={profile.user_id}
                    reportedUserId={profile.user_id}
                    label="Report"
                    variant="outline"
                    size="default"
                    className="myspace-btn w-full text-[#FF6600] border-[#FF6600] justify-center"
                  />
                </div>
              )}
            </div>

            {/* Contact Table */}
            <div className="myspace-card">
              <h3 className="section-header">★ CONTACTING {profile.name?.toUpperCase()} ★</h3>
              <table className="profile-table w-full text-sm">
                <tbody>
                  <tr>
                    <td>Status:</td>
                    <td className={profile.is_online ? "text-[#00FF00]" : "text-[#999999]"}>
                      {profile.is_online ? "Online Now!" : "Offline"}
                    </td>
                  </tr>
                  <tr>
                    <td>Gender:</td>
                    <td>{profile.gender || "Not specified"}</td>
                  </tr>
                  <tr>
                    <td>Looking for:</td>
                    <td>{profile.interested_in || "Friends"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Center/Right Columns - Main Content */}
          <div className="lg:col-span-2 space-y-4">
            {/* About Me / Bio */}
            {profile.bio && (
              <div className="myspace-card" data-testid="profile-bio">
                <h3 className="section-header">★ ABOUT ME ★</h3>
                <p className="text-[#CCCCFF]">{profile.bio}</p>
              </div>
            )}

            {/* Custom HTML */}
            {profile.custom_html && (
              <div
                className="myspace-card custom-profile-container"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(profile.custom_html) }}
                data-testid="profile-custom-html"
              />
            )}

            {/* Interests */}
            {profile.interests?.length > 0 && (
              <div className="myspace-card" data-testid="profile-interests">
                <h3 className="section-header">★ INTERESTS ★</h3>
                <div className="flex flex-wrap gap-1">
                  {profile.interests.map((interest) => (
                    <span key={interest} className="interest-tag">
                      {interest}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Top 8 Friends */}
            <div className="top-friends-box" data-testid="favorite-friends">
              <h3>★ {profile.name?.toUpperCase()}'S TOP FRIENDS ★</h3>
              {favorites.length > 0 ? (
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {favorites.slice(0, 8).map((friend, index) => (
                    <motion.div
                      key={friend.user_id}
                      whileHover={{ scale: 1.1 }}
                      onClick={() => navigate(`/profile/${friend.user_id}`)}
                      className="text-center cursor-pointer"
                    >
                      <div className="w-16 h-16 mx-auto border-2 border-[#FF66FF] overflow-hidden bg-[#000033]">
                        {friend.picture ? (
                          <img
                            src={friend.picture}
                            alt={friend.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-2xl">
                            😎
                          </div>
                        )}
                      </div>
                      <p className="text-[#FF66FF] text-xs mt-1 truncate">{friend.name}</p>
                      {friend.is_online && (
                        <span className="text-[#00FF00] text-[10px]">● Online</span>
                      )}
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p className="text-[#99CCFF] text-sm mt-2">No friends added yet</p>
              )}
              <button className="text-[#FF66FF] text-xs mt-3 hover:text-[#00FFFF]">
                View All Friends →
              </button>
            </div>

            {/* Photos */}
            {profile.photos?.length > 0 && (
              <div className="myspace-card" data-testid="profile-photos">
                <h3 className="section-header">★ PHOTOS ★</h3>
                <div className="grid grid-cols-3 gap-2">
                  {profile.photos.map((photo, i) => (
                    <div key={`photo-${photo.slice(-12)}-${i}`} className="aspect-square border-2 border-[#6600CC] overflow-hidden glitter-overlay">
                      <img src={photo} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Comments Section */}
            <div className="myspace-card">
              <h3 className="section-header">★ {profile.name?.toUpperCase()}'S COMMENTS ★</h3>
              
              {/* Comment Input */}
              {currentUser && !isOwnProfile && (
                <div className="mb-4">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Leave a comment..."
                    className="myspace-input w-full h-20 resize-none mb-2"
                  />
                  <button className="myspace-btn text-sm">
                    Post Comment
                  </button>
                </div>
              )}

              {/* Sample Comments */}
              <div className="space-y-2">
                <div className="comment-box">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="comment-author">★ R.C ★</span>
                    <span className="comment-date">Jan 6, 2026</span>
                  </div>
                  <p className="text-[#CCCCFF] text-sm">Welcome to OurSpacez! Nice profile! 😎</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Music Player */}
      {profile.profile_song && (
        <div className="music-player" data-testid="music-player">
          <div className="container mx-auto flex items-center gap-4">
            <div className="w-10 h-10 bg-[#FF00FF] border-2 border-[#FF66FF] flex items-center justify-center">
              <Music className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <Marquee speed={30} gradient={false}>
                <span className="text-[#00FFFF] font-bold mr-8">
                  ♫ Now Playing: {profile.profile_song.title} - {profile.profile_song.artist} ♫
                </span>
              </Marquee>
            </div>
            <div className="flex gap-2">
              <button className="myspace-btn px-3 py-1 text-sm">
                ▶ Play
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
